import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AccountSidebar from "../../components/AccountSidebar";
import { toast } from "react-hot-toast";
import axiosInstance, { weburl } from "../../../axiosInstance";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { Helmet } from "react-helmet";
import LoadingScreen from "../../components/LoadingScreen";
import { useBlogContext } from "../../../fetchdata/BlogContext";
import { SchedulePickerToast } from "../../components/SchedulePickerToast";

const AccountBookingView = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();

  // ✅ socket context
  const { sendMessage, allMessages } = useBlogContext();

  const [formData, setFormData] = useState({ reason: "", comment: "" });
  const [Order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Reject modal state (like admin)
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTargetIndex, setRejectTargetIndex] = useState(null); // addProduct index
  const [rejectOption, setRejectOption] = useState(null); // 1 cancel | 0 reschedule
  const [rejectReason, setRejectReason] = useState("");

  // -----------------------------
  // helpers
  // -----------------------------
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const getStatusText = (s) =>
    s === 0
      ? "Processing"
      : s === 1
      ? "Completed"
      : s === 2
      ? "Cancelled"
      : s === 3
      ? "Reschedule"
      : "Unknown";

  const getStatusClass = (s) =>
    s === 2
      ? "bg-danger"
      : s === 1
      ? "bg-success text-white"
      : s === 0
      ? "bg-primary"
      : s === 3
      ? "bg-warning text-dark"
      : "bg-secondary text-white";

  const getTypeText = (t) =>
    t === 1
      ? "Order"
      : t === 2
      ? "Rental"
      : t === 3
      ? "Appointment"
      : t === 4
      ? "Nurse"
      : "Unknown";

  // product status (service-level)
  const getProductStatusText = (s) =>
    s === 0
      ? "Pending"
      : s === 1
      ? "Done"
      : s === 2
      ? "Cancelled"
      : s === 3
      ? "Reschedule"
      : "Pending";

  const getProductStatusClass = (s) =>
    s === 1
      ? "bg-success text-white"
      : s === 2
      ? "bg-danger text-white"
      : s === 3
      ? "bg-warning text-dark"
      : "bg-primary text-white";

  const normalizeSubType = (st) => (st || "").toLowerCase();

  // queue status (hold/priority/remove)
  const getQueueSubTypeText = (subType) => {
    const st = normalizeSubType(subType);
    if (st === "hold") return "On Hold";
    if (st === "priority") return "Priority";
    if (st === "remove") return "Removed";
    if (st === "unhold" || st === "unpriority" || !st) return "In Queue";
    return "In Queue";
  };

  const getQueueSubTypeClass = (subType) => {
    const st = normalizeSubType(subType);
    if (st === "hold") return "bg-warning text-dark";
    if (st === "priority") return "bg-success text-white";
    if (st === "remove") return "bg-danger text-white";
    return "bg-secondary text-white";
  };

  // -----------------------------
  // API
  // -----------------------------
  const getUserOrders = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(`/order-quee-view/${orderId}`);
      if (data?.success){
        setOrder(data?.order);
        setAddProduct(data?.order?.addProduct ?? []);
      } 
    } catch (error) {
      console.log(error);
      toast.error("order Not found");
      navigate("/account");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------
  // ✅ REALTIME SOCKET MERGE (NO UI CHANGE)
  // - listens to allMessages and updates this Order state
  // -----------------------------
  useEffect(() => {
    if (!Order?._id || !Array.isArray(allMessages) || allMessages.length === 0) return;

    const orderMongoId = String(Order._id);
    const orderToken = String(Order.orderId || "");

    // Only messages for THIS order (either mongoId OR token)
    const events = allMessages.filter((m) => {
      const oid = m?.orderId;
      if (!oid) return false;
      return String(oid) === orderMongoId || (orderToken && String(oid) === orderToken);
    });

    if (!events.length) return;

    setOrder((prev) => {
      if (!prev) return prev;

      const addProduct = Array.isArray(prev.addProduct) ? [...prev.addProduct] : [];
      let orderStatus = prev.status;

      events.forEach((msg) => {
        const sub = normalizeSubType(msg?.subType);
        const idx = msg?.productIndex;

        // ✅ Full order cancel (optional socket event)
        if (sub === "cancel_order") {
          orderStatus = 2;
        }

        // productIndex required for doctor-wise updates
        if (idx === undefined || idx === null) return;
        if (!addProduct[idx]) return;

        // HOLD / UNHOLD / PRIORITY / UNPRIORITY / REMOVE => subType on product
        if (sub === "hold" || sub === "unhold" || sub === "priority" || sub === "unpriority" || sub === "remove") {
          addProduct[idx] = { ...addProduct[idx], subType: sub };
          return;
        }

        // CANCEL / COMPLETE / RESCHEDULE => status on product
        if (sub === "cancel") {
          addProduct[idx] = { ...addProduct[idx], status: 2 };
          return;
        }
        if (sub === "complete") {
          addProduct[idx] = { ...addProduct[idx], status: 1 };
          return;
        }
        if (sub === "reschedule") {
          addProduct[idx] = { ...addProduct[idx], status: 3 };
          return;
        }
      });

      return { ...prev, status: orderStatus, addProduct };
    });
  }, [allMessages, Order?._id, Order?.orderId]);

  // -----------------------------
  // FULL ORDER CANCEL (existing) + socket
  // -----------------------------
  const CancelStatusChange = async () => {
    setLoading(true);
    try {
      await axiosInstance.put(`/cancel-order/${orderId}`, formData);

      // ✅ socket: full order cancel (optional)
      sendMessage?.({
        type: "appointment",
        subType: "cancel_order",
        orderId: Order?._id || orderId,
        hosId: Order?.hosId?._id,
      });

      toast.success("Order Cancel success!");
      await getUserOrders();
      window.location.reload();
    } catch (error) {
      console.error("Error Order Cancel:", error);
      toast.error("Cancel failed");
    } finally {
      setLoading(false);
    }
  };

  /**
   * ✅ per doctor/product: HOLD / UNHOLD
   * PUT /admin/update-appointment/:id with { mystatus, productIndex }
   * + socket emit
   */
  const updateProductSubType = async (productIndex, mystatus) => {
    if (!Order?._id) return toast.error("Order not found");

    const idx = Number(productIndex);
    if (Number.isNaN(idx) || idx < 0 || idx >= (Order?.addProduct?.length || 0)) {
      return toast.error("Invalid product index");
    }

    try {
      setLoading(true);

      await axiosInstance.put(`/admin/update-appointment/${Order._id}`, {
        mystatus, // "hold" | "unhold"
        productIndex: idx,
      });

      // ✅ socket
      sendMessage?.({
        type: "appointment",
        subType: mystatus, // hold | unhold
        orderId: Order._id, // mongo id is best
        productIndex: idx,
        hosId: Order?.hosId?._id,
      });

      // instant UI update (optimistic)
      setOrder((prev) => {
        if (!prev) return prev;
        const copy = { ...prev, addProduct: [...(prev.addProduct || [])] };
        const p = { ...(copy.addProduct[idx] || {}) };
        p.subType = mystatus;
        copy.addProduct[idx] = p;
        return copy;
      });

      await getUserOrders();
    } catch (error) {
      console.error("Hold/Unhold error:", error);
      toast.error("Failed to update hold");
    } finally {
      setLoading(false);
    }
  };

  /**
   * ✅ Reject (per doctor/service)
   * Cancel => status: 2
   * Reschedule => status: 3
   * + socket emit
   */
  const rejectDoctorService = async (productIndex, rejectChoice, reasonText) => {
    if (!Order?._id) return toast.error("Order not found");

    const idx = Number(productIndex);
    if (Number.isNaN(idx) || idx < 0 || idx >= (Order?.addProduct?.length || 0)) {
      return toast.error("Invalid product index");
    }

    const statusToSend = rejectChoice === 1 ? 2 : 3;

    try {
      setLoading(true);

      await axiosInstance.put(`/admin/update-appointment/${Order._id}`, {
        status: statusToSend,
        reason: reasonText || "",
        productIndex: idx,
      });

      // ✅ socket
      sendMessage?.({
        type: "appointment",
        subType: statusToSend === 2 ? "cancel" : "reschedule",
        orderId: Order._id,
        productIndex: idx,
        hosId: Order?.hosId?._id,
        reason: reasonText || "",
      });

      toast.success(rejectChoice === 1 ? "Cancelled" : "Reschedule requested");

      // instant UI update (optimistic)
      setOrder((prev) => {
        if (!prev) return prev;
        const copy = { ...prev, addProduct: [...(prev.addProduct || [])] };
        const p = { ...(copy.addProduct[idx] || {}) };
        p.status = statusToSend;
        copy.addProduct[idx] = p;
        return copy;
      });

      await getUserOrders();
    } catch (error) {
      console.error("Reject error:", error);
      toast.error("Failed to update reject");
    } finally {
      setLoading(false);
    }
  };

  const openRejectModal = (idx) => {
    setRejectTargetIndex(idx);
    setRejectOption(null);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectTargetIndex(null);
    setRejectOption(null);
    setRejectReason("");
  };

  const submitReject = async () => {
    if (rejectTargetIndex === null || rejectTargetIndex === undefined) return;
    if (rejectOption === null) return toast.error("Please select Cancel or Reschedule");
    if (rejectOption === 1 && !rejectReason.trim()) return toast.error("Please provide a reason for cancellation");

    await rejectDoctorService(rejectTargetIndex, rejectOption, rejectReason.trim());
    closeRejectModal();
  };

  const cancelSuggestions = [
    "Doctor unavailable",
    "Patient not available",
    "Emergency case",
    "Schedule conflict",
    "Health issue",
    "Personal reason",
  ];

  // -----------------------------
  // UI: derived products (unchanged features)
  // -----------------------------
  const derivedProducts = useMemo(() => {
    const list = Order?.addProduct || [];
    return list.map((p, idx) => {
      const queueText = getQueueSubTypeText(p?.subType);
      const queueClass = getQueueSubTypeClass(p?.subType);

      const prodText = getProductStatusText(p?.status);
      const prodClass = getProductStatusClass(p?.status);

      const isHold = normalizeSubType(p?.subType) === "hold";

      // allow actions only when order is processing AND this product is pending
      const canAct = Order?.status === 0 && (p?.status === 0 || p?.status === undefined);

      return {
        ...p,
        _uiIndex: idx,
        _queueText: queueText,
        _queueClass: queueClass,
        _prodText: prodText,
        _prodClass: prodClass,
        _isHold: isHold,
        _canAct: canAct,
      };
    });
  }, [Order]);


      
           const [clinicData, setClinicData] = useState({});
     
            const [allSchedule, setAllSchedule] = useState([]);
        
                 const [loadingSchedule, setLoadingSchedule] = useState(false);
   const [AddProduct, setAddProduct] = useState([]);
 
   const FetchDoctorBookChange = async (myid) => { 
          try {
         
            setLoadingSchedule(true);
           const response = await axiosInstance.get(`/get-vendor-slot?doc=${myid}&days=10&hos=${Order.hosId._id}`);
           const { success, schedule } = response.data;
           if (success) {
          setAllSchedule(schedule ?? []);
             } 
    
             const myschedule = schedule ?? [];
            return myschedule;
         } catch (error) {
           console.error('Error fetching bookings:', error);
       
         }finally{
          setLoadingSchedule(false);
         }
        
       };

         
function to24Hour(t) {
  let [time, modifier] = t.trim().split(" ");
  let [hours, minutes] = time.split(":");

  hours = parseInt(hours, 10);

  if (modifier === "PM" && hours !== 12) {
    hours += 12;
  }
  if (modifier === "AM" && hours === 12) {
    hours = 0;
  }

  return `${hours.toString().padStart(2, "0")}:${minutes}`;
}


        const handleEmployeeChange = async (index, selectedOption) => {
          const employeeId = selectedOption?.value;
          const employee = selectedOption?.data;
        
          console.log('selectedOption',selectedOption);
          if (!employeeId) {
            toast.error("Please select an employee");
            return;
          }
        
          const scheduleList = await FetchDoctorBookChange(employeeId);
           toast.custom((t) => (
            <SchedulePickerToast
              t={t}
              scheduleList={scheduleList}
              employeeId={employeeId}
              employee={employee}
              employeeName={selectedOption?.label}
              onConfirm={({ date, time }) => {
                const updated = [...AddProduct];
        const [start, end] = time.split("-");
        
        const startTime24 = to24Hour(start);
        const endTime24 = to24Hour(end);
        
                // ✅ choose date/time first, then update index
                updated[index] = {
                  ...updated[index],
                  date,
                  time : (employee?.type === 1 && employee?.empType === 3) ?  time : '' ,
                  startTime: (employee?.type === 1 && employee?.empType === 3) ? startTime24 : '',
                  endTime: (employee?.type === 1 && employee?.empType === 3) ? endTime24 : '',
                  employee: selectedOption,
                  employeeId,
                  doctor:  (employee?.type === 1 && employee?.empType === 3) ? 1 : 0,
                };
        
                setAddProduct(updated);
                updateDoctorDateTime(updated);
                // toast.success("Date & time selected");
                console.log('Date & time selected',updated)
              }}
            />
          ),
          {
            duration: Infinity, // ✅ important: no auto-hide
          });
        }; 
        
    const updateDoctorDateTime = async (prodata) => {
 
     try {
      const {data} = await axiosInstance.put(`/admin/update-order-product/${orderId}`, {addProduct:prodata});
 
      if (data?.success) {
      toast.success("Date & time changed successfully!");
        await getUserOrders();
        }
    
     } catch (error) {
      console.error("Error Order Cancel:", error);
      toast.error("something went wrong!");
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <Header page="none" />
      <Helmet>
        <title>Account Order View | {window.location.hostname}</title>
      </Helmet>

      {/* ✅ Reject Modal (like admin) */}
      {showRejectModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reject / Cancel</h5>
                <button type="button" className="btn-close" onClick={closeRejectModal} />
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold d-block">Option</label>
                  <div className="btn-group gap-2" role="group">
                    <button
                      type="button"
                      className={`btn btn-outline-primary rounded-pill shadow-none px-3 ${
                        rejectOption === 1 ? "active opacity-100" : "opacity-75"
                      }`}
                      onClick={() => setRejectOption(1)}
                    >
                      <i className="ri-close-circle-line"></i> Cancel
                    </button>

                    <button
                      type="button"
                      className={`btn btn-outline-primary shadow-none rounded-pill px-3 ${
                        rejectOption === 0 ? "active opacity-100" : "opacity-75"
                      }`}
                      onClick={() => setRejectOption(0)}
                    >
                      <i className="ri-restart-line"></i> Reschedule
                    </button>
                  </div>
                </div>

                {rejectOption === 1 && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Cancel Reason</label>

                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {cancelSuggestions.map((item, i) => (
                        <button
                          key={i}
                          type="button"
                          className="btn btn-sm btn-outline-primary shadow-none rounded-pill"
                          onClick={() => setRejectReason(item)}
                        >
                          {item}
                        </button>
                      ))}
                    </div>

                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Write cancel reason..."
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                    />
                  </div>
                )}

                {rejectOption === 0 && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Reschedule Note (optional)</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Any message for reschedule..."
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeRejectModal}>
                  Close
                </button>
                <button type="button" className="btn text-white btn-success" onClick={submitReject}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="user-dasboard whitesmoke">
        <div className="container pt-md-4">
          <div className="row pb-4">
            <AccountSidebar />

            <div className="col-lg-9 my-0 p-0">
              <div id="main-content" className="bg-white p-0 border">
                {loading ? (
                  <LoadingScreen />
                ) : (
                  <div className="ov-page">
                    {/* Top Header */}
                    <div className="ov-topbar">
                      <button className="ov-back" onClick={() => navigate(-1)} type="button">
                        <i className="ri-arrow-left-line"></i>
                      </button>

                      <div className="ov-titleWrap">
                        <div className="ov-title">Order ID #{Order?.orderId || orderId}</div>
                        {/* <div className="ov-subtitle"></div> */}
                      </div>

                      <a className="ov-iconBtn" href={`${window.location.origin}/account`} title="Account">
                        <i className="ri-user-3-line"></i>
                      </a>
                    </div>

                    {/* Status + Quick actions */}
                    <div className="bg-white rounded-4 shadow-sm p-4 mb-2">
                      <div className="ov-statusRow">
                        <span className={`ov-pill text-white border ${getStatusClass(Order?.status)}`}>
                          {getStatusText(Order?.status)}
                        </span>

                        <span className="ov-pill light">
                          <i className="ri-price-tag-3-line"></i>
                          {getTypeText(Order?.type)}
                        </span>
                      </div>

                      <div className="ov-quick col-md-6">
                        <a
                          className="btn btn-sm btn-dark border rounded-3"
                          href={`${weburl}user-invoice/${Order?._id || orderId}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <i className="ri-file-text-line"></i> Invoice
                        </a>

                        {Order?.prescription && Order?.prescription?.length !== 0 && (
                          <a
                            className="btn btn-sm btn-primary rounded-3"
                            href={`${weburl}user-prescription/${Order?._id || orderId}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <i className="ri-file-text-line"></i> Prescription
                          </a>
                        )}

                        <button className="btn btn-sm btn-white border rounded-3" type="button" onClick={() => getUserOrders()}>
                          <i className="ri-refresh-line"></i> Refresh
                        </button>
                      </div>
                    </div>

                    {/* ✅ EACH DOCTOR SEPARATE: HOLD + REJECT + STATUS */}
                    {derivedProducts.map((product) => {
                      const idx = product._uiIndex;
                      const estimate = product?.employee?.data?.estimate ?? 15;
                      const position = product?.position ?? 0;
                      const estWait = position > 0 ? (position - 1) * estimate : 0;

                      return (
                        <div key={product?._id || idx} className="ov-card">
                          <div className="ov-cardHead">
                            <div className="ov-cardTitle">
                              <i className="ri-stethoscope-line"></i> Doctor / Staff
                            </div>

                            {/* ✅ CURRENT STATUS UI */}
                            <div className="d-flex gap-2 flex-wrap align-items-center">
                              {product?.prescription && product?.prescription?.length !== 0 && (
                                <a
                                  className="btn btn-sm btn-primary rounded-3"
                                  href={`${weburl}user-prescription/${Order?._id || orderId}/${idx}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <i className="ri-file-text-line"></i> Prescription
                                </a>
                              )}

                              <span className={`apx-status ${product._queueClass}`}>{product._queueText}</span>
                              <span className={`apx-status ${product._prodClass}`}>{product._prodText}</span>

                              {/* ✅ HOLD / UNHOLD per doctor */}
                              {product._canAct && (
                                <button
                                  type="button"
                                  className={`btn btn-sm ${product._isHold ? "btn-warning" : "btn-outline-warning"}`}
                                  onClick={() => updateProductSubType(idx, product._isHold ? "unhold" : "hold")}
                                  title="Hold/Unhold this doctor queue only"
                                >
                                  {product._isHold ? (
                                    <>
                                      <i className="ri-play-circle-line"></i> Unhold
                                    </>
                                  ) : (
                                    <>
                                      <i className="ri-pause-circle-line"></i> Hold
                                    </>
                                  )}
                                </button>
                              )}

                              {/* ✅ REJECT per doctor/service (Cancel/Reschedule modal) */}
                              {product._canAct && (
                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => openRejectModal(idx)}>
                                  <i className="ri-close-circle-line"></i> Reject
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="ov-row mt-3">
                            <div className="ov-rowLeft bg-white">
                              <div className="ov-person">
                                <div className="ov-avatar">
                                  <img
                                    src={
                                      product?.employee?.data?.profile
                                        ? weburl + product?.employee?.data?.profile
                                        : "/assets/img/profile.webp"
                                    }
                                    alt="Doctor"
                                  />
                                </div>

                                <div className="ov-personInfo">
                                  <div className="ov-name">{product?.employee?.label ?? "NA"}</div>
                                  <div className="small text-muted">Service: {product?.title ?? "-"}</div>
                                  <div className="small text-muted">Product Index: {idx}</div>
                                </div>
                              </div>
                            </div>

                            <div className="ov-rowRight">
                              <div className="d-flex align-items-center">
                                <div className="col border-end">
                                  <div
                                    className="progress col-6"
                                    role="progressbar"
                                    aria-valuenow={25}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    style={{ height: 8 }}
                                  >
                                    <div className="progress-bar bg-success" style={{ width: "25%" }} />
                                  </div>

                                  <label className="small fw-bold">Position : {position || "-"}</label>
                                  <p className="text-muted m-0 small">Est. Wait : {position ? `${estWait} min` : "-"}</p>
                                </div>

                                <div className="col border-left ps-3">
                                  <p className="text-muted m-0 small">Time: {product?.time ? product?.time : "Any Time"}</p>
                                  <p className="text-muted m-0 small">Date: {product?.date ? formatDate(product.date) : "-"}</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="ov-row mt-3">
                            <div className="ov-rowLeft">
                              <div className="ov-rowLabel">Date</div>
                              <div className="ov-rowVal">
                                <div className="d-flex justify-content-between align-items-center">
                                  {product?.date ? formatDate(product.date) : "-"} <button onClick={()=>handleEmployeeChange(idx,product.employee)} className="border-0 btn btn-primary btn-sm btn-circle"> Reschedule <i className="ri-calendar-schedule-fill"></i> </button></div>
                            </div>
                            </div>
                            <div className="ov-rowRight">
                              <div className="ov-rowLabel">Time</div>
                              <div className="ov-rowVal">{product?.time ? product?.time : "Any Time"}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* User Card */}
                    <div className="ov-card">
                      <div className="ov-cardHead">
                        <div className="ov-cardTitle">
                          <i className="ri-user-3-line"></i> Patient / User
                        </div>
                      </div>

                      <div className="ov-userGrid">
                        <div className="ov-userItem">
                          <div className="ov-userLabel">Name</div>
                          <div className="ov-userVal">{Order?.UserDetails?.[0]?.name || "-"}</div>
                        </div>
                        <div className="ov-userItem">
                          <div className="ov-userLabel">Phone</div>
                          <div className="ov-userVal">{Order?.UserDetails?.[0]?.phone || "-"}</div>
                        </div>
                        <div className="ov-userItem">
                          <div className="ov-userLabel">Email</div>
                          <div className="ov-userVal">{Order?.UserDetails?.[0]?.email || "-"}</div>
                        </div>
                      </div>
                    </div>

                    {/* Cancel full order (existing) */}
                    {Order?.status === 0 && (
                      <div className="ov-card ov-danger d-none">
                        <div className="ov-cardHead">
                          <div className="ov-cardTitle">
                            <i className="ri-close-circle-line"></i> Cancel Order (Full)
                          </div>
                        </div>

                        <div className="ov-form">
                          <select className="form-control mb-2" name="reason" value={formData.reason} onChange={handleChange}>
                            <option value="">Select reason</option>
                            <option value="Not available">Not available</option>
                            <option value="Changed my mind">Changed my mind</option>
                            <option value="Booked by mistake">Booked by mistake</option>
                            <option value="Other">Other</option>
                          </select>

                          <textarea
                            className="form-control mb-2"
                            placeholder="Add comment (optional)"
                            name="comment"
                            value={formData.comment}
                            onChange={handleChange}
                            rows={3}
                          />

                          <button className="ov-cancelBtn" type="button" onClick={CancelStatusChange} disabled={!formData.reason}>
                            <i className="ri-delete-bin-6-line"></i> Cancel Order
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default AccountBookingView;
